"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Image from "@modules/common/components/image-loader"
import SelectItemLink from "./select-item-link"
import type { GtmItem } from "@lib/analytics/gtm"

// Shimmer placeholder shown in deferred slides until their photo is requested.
const ShimmerFill = () => (
  <span
    aria-hidden="true"
    className="absolute inset-0"
    style={{
      background:
        "linear-gradient(90deg, #F0EDE9 25%, #E8E4DF 50%, #F0EDE9 75%)",
      backgroundSize: "200% 100%",
      animation: "alif-shimmer 1.6s ease-in-out infinite",
    }}
  />
)

type Props = {
  images: string[]
  href: string
  alt: string
  sizes?: string
  priority?: boolean
  // select_item payload + list context, mirrored from the surrounding
  // ListViewTracker so an image-slide tap records the selection.
  item?: GtmItem
  listId?: string
  listName?: string
}

// Product-card image gallery.
// • Mobile: native horizontal swipe via CSS scroll-snap. The browser cleanly
//   separates a TAP (follows the link → opens the product) from a SWIPE (scrolls
//   the track), so there is no sticky-:hover "shows the 2nd photo / needs two
//   taps" bug that the old desktop hover-swap caused on touch screens.
// • Desktop: prev/next arrows revealed on hover.
// • Loading: photo 1 loads up front (priority on the first row); photos 2+ are
//   loading="lazy" so they download only when the shopper swipes to them — the
//   grid paints the first photo of every product immediately instead of pulling
//   3–4 photos per card at once.
// • All slides go through the Next.js optimizer (AVIF/WebP + width-matched srcset)
//   via `optimize`, so a card downloads a ~few-KB image instead of the full-size
//   original.
export default function ProductCardGallery({
  images,
  href,
  alt,
  sizes = "(min-width: 1024px) 33vw, 50vw",
  priority,
  item,
  listId,
  listName,
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  // Photos 2+ don't fetch until the card is idle or the shopper interacts, so the
  // grid paints every product's FIRST photo first, then the rest stream in after.
  const [revealRest, setRevealRest] = useState(false)
  const count = images.length

  useEffect(() => {
    if (count <= 1 || revealRest) return
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    const reveal = () => setRevealRest(true)
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(reveal, { timeout: 2500 })
      return () => w.cancelIdleCallback?.(id)
    }
    const id = window.setTimeout(reveal, 1800)
    return () => window.clearTimeout(id)
  }, [count, revealRest])

  const onScroll = useCallback(() => {
    setRevealRest(true) // a swipe means the shopper wants the other photos now
    const el = trackRef.current
    if (!el || el.clientWidth === 0) return
    const i = Math.round(el.scrollLeft / el.clientWidth)
    setActive((prev) => (prev === i ? prev : i))
  }, [])

  const goTo = useCallback((i: number) => {
    const el = trackRef.current
    if (!el) return
    const next = Math.max(0, Math.min(i, el.children.length - 1))
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" })
  }, [])

  if (count === 0) {
    return <div className="absolute inset-0 bg-white" />
  }

  return (
    <>
      {/* Clarity's Balanced masking hides images, which for a clothing store
          blanks out the one thing a recording is worth watching for. Product
          photography is public catalogue content — nothing shopper-entered
          lives in here — so it is explicitly unmasked. Do NOT lift this to a
          shared image component: it would also unmask anything rendered on
          /checkout or /account. */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        data-clarity-unmask="true"
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((url, i) => (
          <SelectItemLink
            key={`${url}-${i}`}
            href={href}
            item={item}
            listId={listId}
            listName={listName}
            tabIndex={i === 0 ? undefined : -1}
            aria-label={i === 0 ? alt : `${alt}, photo ${i + 1}`}
            className="relative block h-full w-full shrink-0 snap-center outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#3D5240]"
          >
            {i === 0 || revealRest ? (
              <Image
                src={url}
                alt={i === 0 ? alt : ""}
                fill
                sizes={sizes}
                quality={82}
                optimize
                className="object-cover"
                priority={priority && i === 0}
                loading={priority && i === 0 ? undefined : "lazy"}
                aria-hidden={i === 0 ? undefined : true}
              />
            ) : (
              <ShimmerFill />
            )}
          </SelectItemLink>
        ))}
      </div>

      {count > 1 && (
        <>
          {/* Desktop arrows (small: = 1024px). Mobile uses swipe + dots. */}
          <button
            type="button"
            aria-label="Previous photo"
            onClick={() => goTo(active - 1)}
            disabled={active === 0}
            className="absolute left-2 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#1A1612] opacity-0 shadow-sm transition-opacity duration-150 hover:bg-white disabled:opacity-0 small:flex small:group-hover:opacity-100 small:group-hover:disabled:opacity-30"
          >
            <span aria-hidden="true" className="-mt-0.5 text-lg leading-none">‹</span>
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={() => goTo(active + 1)}
            disabled={active === count - 1}
            className="absolute right-2 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#1A1612] opacity-0 shadow-sm transition-opacity duration-150 hover:bg-white disabled:opacity-0 small:flex small:group-hover:opacity-100 small:group-hover:disabled:opacity-30"
          >
            <span aria-hidden="true" className="-mt-0.5 text-lg leading-none">›</span>
          </button>

          {/* Position dots */}
          <div className="pointer-events-none absolute inset-x-0 bottom-2.5 z-20 flex items-center justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to photo ${i + 1}`}
                aria-current={i === active}
                onClick={() => goTo(i)}
                className={`pointer-events-auto h-1.5 rounded-full shadow-[0_0_2px_rgba(0,0,0,0.35)] transition-all duration-200 ${
                  i === active ? "w-4 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </>
  )
}
